import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Briefcase, Plus, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { MISSION_STATUTS } from './AdrnsMissions';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdrnsDossierMissions({ dossierId }) {
    const { session } = useAuth();
    const [items, setItems] = useState(null);
    const [types, setTypes] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ type: 'creation_entreprise', titre: '', objectif: '', modules: [] });
    const [saving, setSaving] = useState(false);
    const hdrs = () => ({ headers: { Authorization: `Bearer ${session.access_token}` } });

    const reload = () => axios.get(`${API}/adrns/dossiers/${dossierId}/missions`, hdrs())
        .then(r => setItems(r.data.items)).catch(() => setItems([]));
    useEffect(() => {
        if (!session) return;
        reload();
        axios.get(`${API}/adrns/missions/types`, hdrs()).then(r => setTypes(r.data.types)).catch(() => {});
        // eslint-disable-next-line
    }, [session, dossierId]);

    const selected = types.find(t => t.key === form.type);

    const create = async () => {
        setSaving(true);
        try {
            const body = { type: form.type, titre: form.titre || undefined, objectif: form.objectif || undefined };
            if (form.type === 'sur_mesure') body.modules = form.modules;
            await axios.post(`${API}/adrns/dossiers/${dossierId}/missions`, body, hdrs());
            toast.success('Mission ouverte — modules activés automatiquement');
            setOpen(false);
            setForm({ type: 'creation_entreprise', titre: '', objectif: '', modules: [] });
            reload();
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
        setSaving(false);
    };

    return (
        <div className="card-soft p-6" data-testid="dossier-missions">
            <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-xl flex items-center gap-2"><Briefcase size={18} className="text-primary" /> Missions d'accompagnement</h2>
                <Button size="sm" onClick={() => setOpen(true)} data-testid="mission-new-btn">
                    <Plus size={13} className="mr-1.5" /> Ouvrir une mission
                </Button>
            </div>
            {items === null ? <Loader2 className="animate-spin mx-auto" size={16} /> : items.length === 0 ? (
                <p className="text-sm text-foreground/60">Aucune mission. Ouvrez une mission : l'Intelligence 360° activera les modules nécessaires et suivra les livrables attendus.</p>
            ) : (
                <div className="space-y-2">
                    {items.map(m => (
                        <Link key={m.id} to={`/app/adrns/dossiers/${dossierId}/missions/${m.id}`}
                            className="flex items-center gap-3 border border-border rounded-lg px-3 py-2.5 text-sm hover:border-primary/40 transition-colors"
                            data-testid={`dossier-mission-${m.id}`}>
                            <Briefcase size={15} className="text-primary shrink-0" />
                            <div className="flex-1 min-w-0">
                                <div className="font-medium truncate">{m.titre}</div>
                                <div className="text-xs text-muted-foreground">{m.type_nom} · {m.livrables_valides}/{m.livrables_total} livrables validés</div>
                            </div>
                            <span className="text-sm font-medium">{m.avancement}%</span>
                            <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${(MISSION_STATUTS[m.statut] || {}).cls}`}>
                                {(MISSION_STATUTS[m.statut] || {}).label}
                            </span>
                            <ArrowRight size={13} className="text-muted-foreground" />
                        </Link>
                    ))}
                </div>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="sm:max-w-lg" data-testid="mission-create-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading">Ouvrir une mission</DialogTitle>
                        <DialogDescription>L'Intelligence African Valley 360° active automatiquement les modules et les livrables attendus.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                            className="h-10 w-full px-3 rounded-md border border-input bg-background text-sm" data-testid="mission-type-select">
                            {types.map(t => <option key={t.key} value={t.key}>{t.nom}</option>)}
                        </select>
                        {selected && form.type !== 'sur_mesure' && (
                            <div className="rounded-lg border border-border bg-surface-muted/40 px-3 py-2 text-xs text-foreground/70" data-testid="mission-playbook-preview">
                                <b>Playbook :</b> modules {selected.modules.map(x => `M${x}`).join(' + ')} · {selected.livrables_cles.length} livrables attendus
                            </div>
                        )}
                        {form.type === 'sur_mesure' && (
                            <div className="rounded-lg border border-border px-3 py-2 space-y-1.5">
                                <div className="text-xs font-medium">Modules à mobiliser :</div>
                                {[1, 2, 3, 4, 5, 6].map(mid => (
                                    <label key={mid} className="flex items-center gap-2 text-sm">
                                        <Checkbox checked={form.modules.includes(mid)}
                                            onCheckedChange={v => setForm(f => ({ ...f, modules: v ? [...f.modules, mid] : f.modules.filter(x => x !== mid) }))}
                                            data-testid={`mission-module-check-${mid}`} />
                                        Module {mid}
                                    </label>
                                ))}
                            </div>
                        )}
                        <Input placeholder="Titre de la mission (optionnel)" value={form.titre} onChange={e => setForm(f => ({ ...f, titre: e.target.value }))} data-testid="mission-form-titre" />
                        <Textarea rows={3} placeholder="Objectif de la mission (optionnel)" value={form.objectif} onChange={e => setForm(f => ({ ...f, objectif: e.target.value }))} data-testid="mission-form-objectif" />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
                        <Button onClick={create} disabled={saving || (form.type === 'sur_mesure' && form.modules.length === 0)} data-testid="mission-form-submit">
                            {saving ? <Loader2 size={14} className="animate-spin" /> : 'Ouvrir la mission'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
